import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  parseMessageFromNative,
  sendMessageToParent,
} from "src/helpers/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import Switch from "../../Switch";
import Button from "../../Button";
import BluetoothConnectionBottomSheet from "../../bottom-sheet/cgm/BluetoothConnectionBottomSheet";

type BluetoothStepProps = {
  onStatusChange: (status: "unknown" | "on" | "off" | "denied") => void;
  onProceed?: () => void;
};

type LocationStatus = "unknown" | "on" | "off" | "denied";

const BluetoothStep: React.FC<BluetoothStepProps> = ({
  onStatusChange,
  onProceed,
}) => {
  const [showKnowMoreBottomSheet, setShowKnowMoreBottomSheet] = useState(false);
  const [bluetoothStatus, setBluetoothStatus] = useState<
    "unknown" | "on" | "off" | "denied"
  >("unknown");
  const [locationStatus, setLocationStatus] =
    useState<LocationStatus>("unknown");

  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const canProceed = useMemo(
    () => bluetoothStatus === "on" && locationStatus === "on",
    [bluetoothStatus, locationStatus]
  );

  useEffect(() => {
    onStatusChangeRef.current?.(bluetoothStatus);
  }, [bluetoothStatus]);

  // Poll RN for status (read-only checks; no permission prompts). Slower interval avoids racing user taps.
  useEffect(() => {
    sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_BLUETOOTH_STATUS, {});
    sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_LOCATION_STATUS, {});
    const timer = setInterval(() => {
      sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_BLUETOOTH_STATUS, {});
      sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_LOCATION_STATUS, {});
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = parseMessageFromNative(event.data);
      if (!payload) return;
      const action = (payload.action || payload.type) as string;

      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_ON) {
        setBluetoothStatus("on");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_OFF) {
        setBluetoothStatus("off");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_PERMISSION_DENIED) {
        setBluetoothStatus("denied");
        return;
      }

      if (action === POST_MESSAGE_EVENTS.LOCATION_ON) {
        setLocationStatus("on");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.LOCATION_OFF) {
        setLocationStatus("off");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.LOCATION_PERMISSION_DENIED) {
        setLocationStatus("denied");
        return;
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  /** Native: request BLE + (Android) location permissions, then enable adapter when applicable. */
  const requestBluetooth = () => {
    if (bluetoothStatus === "on") return;
    sendMessageToParent(POST_MESSAGE_EVENTS.REQUEST_BLUETOOTH_PERMISSION, {});
  };

  /**
   * RN maps LOCATION_PERMISSION_DENIED to "denied" (blocked in Settings).
   * "off" = not granted yet or denied once — use system permission prompt first.
   * Blocked users need Settings (TURN_ON_LOCATION).
   */
  const requestLocation = () => {
    if (locationStatus === "on") return;
    if (locationStatus === "denied") {
      sendMessageToParent(POST_MESSAGE_EVENTS.TURN_ON_LOCATION, {});
      return;
    }
    sendMessageToParent(POST_MESSAGE_EVENTS.REQUEST_LOCATION_PERMISSION, {});
  };

  return (
    <div className="px-4 py-4 flex flex-col items-center gap-4 w-full">
      <h1 className="text-2xl font-semibold text-black">Provide Permissions</h1>
      <p className="text-sm text-gray-500 text-center">
        GoodFlip CGM requires bluetooth connection and location permission to
        transmit the data to the app.
      </p>

      <div className="border border-gray-300 rounded-lg p-4 w-full">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">
            Turn On Bluetooth
          </div>
          <Switch
            checked={bluetoothStatus === "on"}
            onChange={() => requestBluetooth()}
          />
        </div>
        <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between gap-4 mt-3">
          <div className="text-xs text-gray-500">
            Having trouble with bluetooth permission?
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => setShowKnowMoreBottomSheet(true)}
              variant="text"
              rounded="full"
              fullWidth
              size="sm"
            >
              <span className="underline">Know more</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 w-full">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">
            Turn On Location
          </div>
          <Switch
            checked={locationStatus === "on"}
            onChange={() => requestLocation()}
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100">
        <Button
          onClick={() => onProceed?.()}
          variant="contained"
          rounded="full"
          fullWidth
          disabled={!canProceed}
        >
          Proceed
        </Button>
      </div>

      <BluetoothConnectionBottomSheet
        show={showKnowMoreBottomSheet}
        handleClose={() => setShowKnowMoreBottomSheet(false)}
      />
    </div>
  );
};

export default BluetoothStep;
